import React, { useEffect, useRef, useState } from 'react';
import '../styles/LandingPage.css';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import ChatWidget from './common/ChatWidget';
import { toast } from 'react-hot-toast';
import api from '../utils/api';

const TypeWriter = () => {
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(100);
  const [isBlinkingCursor, setIsBlinkingCursor] = useState(true);

  const words = [
    'your business',
    'customer support', 
    'lead generation',
    'increasing sales',
    'appointment booking',
    'order tracking',
    'customer service',
    'personal assistance',
    'collecting invoices',
    'sales outreach',
    'recruiting',
    'automating reception',
    'restaurant booking',
    'customer onboarding',
    'creating your digital twin',
    'getting your time back',
    'saving money',
    'saving time',
    'automating your business',
    'automating your sales',
  ];

  useEffect(() => {
    let timeout;
    
    const type = () => {
      const currentWord = words[wordIndex];
      const shouldDelete = isDeleting;
      
      setDisplayText(prev => 
        shouldDelete 
          ? currentWord.substring(0, prev.length - 1)
          : currentWord.substring(0, prev.length + 1)
      );
      
      setTypingSpeed(isDeleting ? 50 : 100);
      
      if (!isDeleting && displayText === currentWord) {
        // Pause at the end of the word
        setTimeout(() => {
          setIsBlinkingCursor(true);
          setTimeout(() => {
            setIsBlinkingCursor(false);
            setIsDeleting(true);
          }, 1500);
        }, 0);
      } else if (isDeleting && displayText === '') {
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % words.length);
        setIsBlinkingCursor(true);
      }
    };
    
    timeout = setTimeout(type, typingSpeed);
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, wordIndex, words, typingSpeed]);

  return (
    <span>
      {displayText}
      <span 
        className={`cursor-blink ${isBlinkingCursor ? 'blink' : ''}`}
        style={{ 
          borderRight: '2px solid currentColor',
          marginLeft: '2px',
          animation: 'blink 1s step-end infinite'
        }}
      >
        &nbsp;
      </span>
    </span>
  );
};

const EmailForm = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Helper function to validate business email
  const isBusinessEmail = (email) => {
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
      'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
      'gmx.com', 'live.com', 'msn.com'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !freeEmailProviders.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!isBusinessEmail(email)) {
      setError('Please use your business email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.subscribeToNewsletter(email);
      setSuccess(true);
      setEmail('');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type="email"
            placeholder="Enter your business email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="email-input"
            required
            disabled={isSubmitting}
          />
          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            )}
            <span className="sr-only">Submit</span>
          </button>
        </div>
        {error && (
          <div className="text-red-500 text-sm text-center mt-2">
            {error}
          </div>
        )}
        {success && (
          <div className="text-green-500 text-sm text-center mt-2">
            Thanks for subscribing! Stay tuned—we'll be in touch soon!
          </div>
        )}
      </form>
    </div>
  );
};

const SeamlessAI = () => {
  const [activeIcon, setActiveIcon] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [mouseAngle, setMouseAngle] = useState(0);

  const handleIconClick = (tool) => {
    setActiveIcon(tool.name);
  };

  const handleImageError = (toolName) => {
    setImageErrors(prev => ({
      ...prev,
      [toolName]: true
    }));
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - (rect.left + rect.width / 2);
    const y = e.clientY - (rect.top + rect.height / 2);
    const angle = Math.atan2(y, x) * (180 / Math.PI);
    setMouseAngle(angle);
  };

  return (
    <div className="seamless-section px-4 py-8 md:py-16 w-full overflow-hidden">
      <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
        <div className="w-full overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-serif mb-4">AI Call Center Magic</h2>
          <p className="text-primary mb-6" style={{ opacity: 0.8 }}>
            Experience the future of customer communication with our AI-powered call center solution.
          </p>
          <div className="relative space-y-4 p-2 md:p-4 rounded-lg">
            {/* Phone Interface */}
            <div className="relative mx-auto w-full max-w-[300px] h-[400px] bg-black rounded-3xl p-4 shadow-2xl border-4 border-zinc-800">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-xl"></div>
              <div className="w-full h-full bg-gradient-to-b from-purple-900/20 to-blue-900/20 rounded-2xl p-4 overflow-hidden">
                {/* Call Status Bar */}
                <div className="flex items-center justify-between text-xs text-white/80 mb-4">
                  <span>AI Call Center</span>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span>Active Call</span>
                  </div>
                </div>
                
                {/* Call Features Showcase */}
                <div className="space-y-4">
                  {/* Language Selection */}
                  <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg animate-slide-in" style={{ '--delay': '0s' }}>
                    <div className="flex items-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      <span className="text-sm">Multilingual Support</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                      {['🇺🇸', '🇪🇸', '🇫🇷', '🇩🇪', '🇯🇵', '🇬🇷', '🇸🇪', '...'].map((flag, i) => (
                        <span key={i} className="text-lg">{flag}</span>
                      ))}
                    </div>
                  </div>

                  {/* Calendar Integration */}
                  <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg animate-slide-in" style={{ '--delay': '0.2s' }}>
                    <div className="flex items-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">Calendar Booking</span>
                    </div>
                    <div className="mt-2 text-xs text-white/70">
                      "Let me schedule that meeting for next Tuesday at 2 PM"
                    </div>
                  </div>

                  {/* API Integration */}
                  <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg animate-slide-in" style={{ '--delay': '0.4s' }}>
                    <div className="flex items-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-sm">Real-time Actions</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <span className="px-2 py-1 bg-white/20 rounded text-xs text-white/70">CRM Update</span>
                      <span className="px-2 py-1 bg-white/20 rounded text-xs text-white/70">Order Status</span>
                      <span className="px-2 py-1 bg-white/20 rounded text-xs text-white/70">Payments</span>
                    </div>
                  </div>

                  {/* Voice Analysis */}
                  <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg animate-slide-in" style={{ '--delay': '0.6s' }}>
                    <div className="flex items-center gap-2 text-white/90">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      <span className="text-sm">Smart Voice Analysis</span>
                    </div>
                    <div className="mt-2">
                      <div className="flex gap-1">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 bg-purple-500/60 rounded-full animate-pulse"
                            style={{
                              height: `${Math.random() * 20 + 10}px`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full overflow-hidden text-card-foreground shadow-sm p-4 md:p-6 bg-background/50 backdrop-blur-xl rounded-2xl border border-purple-500/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center border border-purple-500/30">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-serif">Integrations with Your Business World</h2>
              <p className="text-primary/60 text-sm">
                Seamlessly connect your AI Call Agent with your favorite tools
              </p>
            </div>
          </div>

          <div className="relative h-[400px] flex items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-purple-900/20 via-transparent to-pink-900/20 border border-purple-500/20" onMouseMove={handleMouseMove}>
            {/* Animated Background Patterns */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-grid-white/5 bg-grid-16 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10 animate-grid" />
            </div>

            <div className="relative z-10">
              {/* Background Rings with Glow */}
              <div className="absolute inset-0 bg-purple-500/10 rounded-full blur-2xl animate-pulse-slow" style={{ transform: 'scale(1.0447)' }}></div>
              <div className="absolute inset-0 bg-pink-500/10 rounded-full blur-xl animate-pulse-slow" style={{ transform: 'scale(1.17588)', animationDelay: '-2s' }}></div>
              <div className="absolute inset-0 bg-blue-500/10 rounded-full blur-lg animate-pulse-slow" style={{ transform: 'scale(1.42262)', animationDelay: '-4s' }}></div>
              
              {/* Central Icon with Enhanced Design */}
              <div className="relative w-24 h-24 bg-black/80 rounded-2xl border border-purple-500/50 flex items-center justify-center backdrop-blur-xl shadow-2xl transform-gpu hover:scale-105 transition-transform">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl animate-gradient"></div>
                <div className="relative">
                  <img 
                    src="/logo.png"
                    alt="Central Icon"
                    className="w-16 h-16 object-contain animate-float"
                    loading="lazy"
                    width={64}
                    height={64}
                  />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-purple-500/0 blur-sm"></div>
                </div>
              </div>
            </div>

            {/* Integration Icons with Enhanced Animations */}
            {[
              { name: 'slack', angle: 0, label: 'Slack Integration', icon: '/integrations/slack.png', color: '#4A154B' },
              { name: 'drive', angle: 60, label: 'Google Drive Integration', icon: '/integrations/drive.png', color: '#1FA463' },
              { name: 'notion', angle: 120, label: 'Notion Integration', icon: '/integrations/notion.png', color: '#000000' },
              { name: 'whatsapp', angle: 180, label: 'WhatsApp Integration', icon: '/integrations/whatsapp.png', color: '#25D366' },
              { name: 'salesforce', angle: 240, label: 'Salesforce Integration', icon: '/integrations/salesforce.png', color: '#00A1E0' },
              { name: 'zapier', angle: 300, label: 'Zapier Integration', icon: '/integrations/zapier.png', color: '#FF4A00' }
            ].map((tool) => {
              const rotationAngle = tool.angle + mouseAngle;
              const x = Math.cos((tool.angle - 90) * (Math.PI / 180)) * 140;
              const y = Math.sin((tool.angle - 90) * (Math.PI / 180)) * 140;

              return (
                <div
                  key={tool.name}
                  role="button"
                  tabIndex={0}
                  aria-label={tool.label}
                  className={`absolute w-16 h-16 rounded-xl flex items-center justify-center shadow-2xl backdrop-blur-md transition-all duration-300 transform-gpu hover:scale-110 ${
                    activeIcon === tool.name ? 'border-2' : 'border'
                  }`}
                  style={{
                    transform: `translateX(${x}px) translateY(${y}px) rotate(${rotationAngle}deg)`,
                    backgroundColor: `${tool.color}20`,
                    borderColor: `${tool.color}50`,
                    boxShadow: activeIcon === tool.name ? `0 0 20px ${tool.color}30` : '',
                  }}
                  onClick={() => handleIconClick(tool)}
                  onKeyDown={(e) => e.key === 'Enter' && handleIconClick(tool)}
                >
                  <div 
                    className="w-full h-full flex items-center justify-center relative"
                    style={{ 
                      transform: `rotate(-${rotationAngle}deg)`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/0 rounded-xl"></div>
                    <img 
                      src={imageErrors[tool.name] ? '/integrations/fallback.png' : tool.icon}
                      alt={tool.label}
                      className="w-8 h-8 object-contain transition-all duration-300"
                      style={{
                        filter: 'brightness(1.2) contrast(1.1)',
                        transform: `scale(${activeIcon === tool.name ? 1.1 : 1})`,
                      }}
                      loading="lazy"
                      width={32}
                      height={32}
                      onError={() => handleImageError(tool.name)}
                    />
                    {activeIcon === tool.name && (
                      <div className="absolute inset-0 rounded-xl ring-2 ring-white/20 ring-offset-2 ring-offset-black/50"></div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Enhanced Connection Lines */}
            {[0, 60, 120, 180, 240, 300].map((angle, index) => (
              <div
                key={angle}
                className="absolute w-[140px] h-[2px] origin-left"
                style={{
                  transform: `translateX(60px) rotate(${angle}deg)`,
                }}
              >
                <div
                  className="h-full w-full"
                  style={{
                    background: `linear-gradient(90deg, rgba(139, 92, 246, 0.5), rgba(139, 92, 246, 0))`,
                    transform: `scaleX(${Math.abs(Math.cos((angle + mouseAngle) * (Math.PI / 180)))})`,
                    opacity: 0.5,
                  }}
                >
                  <div 
                    className="absolute top-1/2 left-1/4 w-1 h-1 rounded-full bg-purple-400 animate-pulse"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  />
                </div>
              </div>
            ))}

            {/* Particle Effects */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="particle absolute w-1 h-1 rounded-full bg-purple-400/30"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `float ${5 + Math.random() * 5}s linear infinite`,
                    animationDelay: `-${Math.random() * 5}s`,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Integration Stats */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="stat-card">
              <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">50+</div>
              <div className="text-xs text-gray-400">Tool Integrations</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">∞</div>
              <div className="text-xs text-gray-400">Webhook Calls</div>
            </div>
            <div className="stat-card">
              <div className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">100%</div>
              <div className="text-xs text-gray-400">Customizable</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Features = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  const features = [
    {
      title: "Intelligent Call Routing",
      description: "AI-powered decision making routes calls to the perfect virtual agent based on context and intent.",
      icon: (
        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      stats: ["99.9%", "Routing Accuracy"],
      animation: (
        <div className="relative w-full h-12">
          <div className="absolute inset-0 flex items-center justify-between">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-400/30">
                  <div className="w-full h-full rounded-full bg-blue-400 animate-ping" 
                       style={{ animationDuration: '1.5s', animationDelay: `${i * 0.2}s` }}/>
                </div>
                {i < 2 && (
                  <div className="h-0.5 w-16 bg-gradient-to-r from-blue-400 to-transparent"/>
                )}
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      title: "Multilingual Support",
      description: "Our AI agents speak multiple languages fluently, handling international calls with ease.",
      icon: (
        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      ),
      stats: ["20+", "Languages"],
      animation: (
        <div className="flex gap-2 overflow-hidden py-2">
          <div className="flex animate-scroll">
            {["🇺🇸", "🇪🇸", "🇫🇷", "🇩🇪", "🇯🇵", "🇮🇳", "🇧🇷", "🇨🇳"].map((flag, i) => (
              <span key={i} className="text-2xl mx-2">{flag}</span>
            ))}
          </div>
          <div className="flex animate-scroll" style={{ animationDelay: "-4s" }}>
            {["🇺🇸", "🇪🇸", "🇫🇷", "🇩🇪", "🇯🇵", "🇮🇳", "🇧🇷", "🇨🇳"].map((flag, i) => (
              <span key={i} className="text-2xl mx-2">{flag}</span>
            ))}
          </div>
        </div>
      )
    },
    {
      title: "Real-time Analytics",
      description: "Monitor call performance, sentiment analysis, and conversion rates in real-time.",
      icon: (
        <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      stats: ["Real-time", "Insights"],
      animation: (
        <div className="flex items-end h-12 gap-1">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="w-4 bg-gradient-to-t from-purple-600 to-purple-400 rounded-t"
              style={{
                height: `${Math.random() * 100}%`,
                animation: `barHeight 1s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
      )
    },
    {
      title: "Smart Integrations",
      description: "Seamlessly connect with your CRM, calendar, and business tools for automated workflows.",
      icon: (
        <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
      stats: ["50+", "Integrations"],
      animation: (
        <div className="relative w-full h-12">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-8 h-8">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="absolute w-full h-full border-2 border-orange-400/30 rounded-lg"
                  style={{
                    animation: "rotate 3s linear infinite",
                    animationDelay: `${i * 0.5}s`,
                    transform: `rotate(${i * 45}deg)`
                  }}
                />
              ))}
              <div className="absolute inset-1 bg-orange-400/20 rounded-lg animate-pulse"/>
            </div>
          </div>
        </div>
      )
    }
  ];

  const startAutoPlay = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      if (!isHovering) {
        setActiveFeature((prev) => (prev + 1) % features.length);
      }
    }, 3000);
  };

  const stopAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleNext = () => {
    setActiveFeature((prev) => (prev + 1) % features.length);
  };

  const handlePrev = () => {
    setActiveFeature((prev) => (prev - 1 + features.length) % features.length);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setActiveFeature(index);
      e.preventDefault();
    }
  };

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [isHovering]);

  return (
    <div className="features-section relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-blue-900/20 pointer-events-none"/>
      <div className="max-w-7xl mx-auto px-4 py-20">
        <span className="inline-block px-3 py-1 text-sm font-medium text-purple-500 bg-purple-900/30 rounded-full">
          Features
        </span>
        <h2 className="text-4xl md:text-5xl lg:text-6xl mt-4 mb-8">
          Your calls. <span className="text-gradient">Supercharged</span>.
        </h2>
        
        <div className="mt-16 space-y-12">
          {/* Feature Carousel */}
          <div 
            className="relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            ref={carouselRef}
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              {features.map((feature, index) => (
                <button
                  key={index}
                  className={`feature-card-mini group transition-all duration-300 w-24 h-24 rounded-xl p-4 flex items-center justify-center
                    ${activeFeature === index 
                      ? 'bg-white/15 scale-110 shadow-lg shadow-purple-500/20' 
                      : 'bg-white/5 hover:bg-white/10'}`}
                  onClick={() => setActiveFeature(index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  aria-label={`View ${feature.title} feature`}
                  tabIndex={0}
                  role="tab"
                  aria-selected={activeFeature === index}
                >
                  <div className={`transform transition-transform duration-300 ${activeFeature === index ? 'scale-110' : 'group-hover:scale-105'}`}>
                    {feature.icon}
                  </div>
                </button>
              ))}
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrev}
              className="feature-carousel-nav absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 p-2 rounded-full bg-white/5 hover:bg-white/10"
              aria-label="Previous feature"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="feature-carousel-nav absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 p-2 rounded-full bg-white/5 hover:bg-white/10"
              aria-label="Next feature"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Feature Showcase */}
          <div className="relative bg-white/5 rounded-2xl p-8 backdrop-blur-sm border border-white/10 transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 rounded-2xl"/>
            <div className="relative">
              {/* Feature Info */}
              <div className="text-center mb-8 max-w-2xl mx-auto">
                <div className="w-16 h-16 rounded-xl bg-white/10 mx-auto mb-6 flex items-center justify-center">
                  {features[activeFeature].icon}
                </div>
                <h3 className="text-2xl font-semibold mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  {features[activeFeature].title}
                </h3>
                <p className="text-gray-400 text-lg">
                  {features[activeFeature].description}
                </p>
              </div>

              {/* Animation Container */}
              <div className="w-full max-w-2xl mx-auto bg-black/20 rounded-xl p-8 backdrop-blur-sm border border-white/10">
                <div className="transform transition-all duration-500">
                  {features[activeFeature].animation}
                </div>
              </div>

              {/* Stats */}
              <div className="mt-8 flex items-center justify-center gap-3">
                <div className="stats-badge px-6 py-3 bg-white/5 rounded-full backdrop-blur-sm border border-white/10">
                  <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    {features[activeFeature].stats[0]}
                  </span>
                  <span className="ml-2 text-sm text-gray-400">{features[activeFeature].stats[1]}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CallToAction = () => (
  <div className="cta-section">
    <div className="cta-container">
      <section className="cta-content">
        <div className="text-center space-y-8">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
            Ready to{' '}
            <span className="gradient-accent">revolutionize</span>{' '}
            your conversations?
          </h2>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Experience the future of AI-powered conversations. Transform how you connect with customers.
          </p>
          <Link
            to="/request-demo"
            className="cta-button"
          >
            <span className="flex items-center justify-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 mr-2" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Book A Demo
            </span>
          </Link>
          <p className="text-sm text-gray-400">
            See Lifelike Agents in action with a personalized demo tailored to your needs.
          </p>
        </div>
      </section>
    </div>
  </div>
);

const ChatDemoPrompt = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    // Change to focus detection instead of timeout
    const handleChatFocus = () => setIsVisible(false);
    
    // Because the chat might load after this component mounts,
    // use a small delay to ensure we can find input elements
    const setupEventListeners = () => {
      // Get the text input field within the chat dialog
      const chatElement = document.getElementById('dialog-chat');
      if (!chatElement) return false;
      
      const textInputs = chatElement.querySelectorAll('input, textarea');
      
      // Add focus event listeners to all input/textarea elements
      textInputs.forEach(input => {
        if (!input.hasAttribute('data-has-focus-listener')) {
          input.addEventListener('focus', handleChatFocus);
          input.setAttribute('data-has-focus-listener', 'true');
        }
      });
      
      // Also listen for click events on the send button
      const sendButton = chatElement.querySelector('button');
      if (sendButton && !sendButton.hasAttribute('data-has-click-listener')) {
        sendButton.addEventListener('click', handleChatFocus);
        sendButton.setAttribute('data-has-click-listener', 'true');
      }
      
      return true;
    };
    
    // Try immediately and then retry after a short delay
    let success = setupEventListeners();
    
    // If initial setup didn't find elements, try again after chat has likely loaded
    const retryTimer = !success ? setTimeout(() => {
      setupEventListeners();
    }, 1000) : null;

    // Handle window resize
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);

    // Set up a mutation observer to watch for dynamically added inputs
    const chatElement = document.getElementById('dialog-chat');
    let observer;
    
    if (chatElement) {
      observer = new MutationObserver(mutations => {
        // Only setup listeners if we see meaningful DOM changes
        let shouldSetup = false;
        
        mutations.forEach(mutation => {
          if (mutation.addedNodes.length || 
              (mutation.type === 'attributes' && 
               mutation.target.tagName && 
               (mutation.target.tagName.toLowerCase() === 'input' || 
                mutation.target.tagName.toLowerCase() === 'textarea'))) {
            shouldSetup = true;
          }
        });
        
        if (shouldSetup) {
          setupEventListeners();
        }
      });
      
      observer.observe(chatElement, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['id', 'class'] 
      });
    }

    return () => {
      // Clean up all event listeners
      const chatElement = document.getElementById('dialog-chat');
      if (chatElement) {
        const textInputs = chatElement.querySelectorAll('input, textarea');
        textInputs.forEach(input => {
          if (input.hasAttribute('data-has-focus-listener')) {
            input.removeEventListener('focus', handleChatFocus);
            input.removeAttribute('data-has-focus-listener');
          }
        });
        
        const sendButton = chatElement.querySelector('button');
        if (sendButton && sendButton.hasAttribute('data-has-click-listener')) {
          sendButton.removeEventListener('click', handleChatFocus);
          sendButton.removeAttribute('data-has-click-listener');
        }
      }
      
      clearTimeout(retryTimer);
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div className="chat-prompt-container" style={{pointerEvents: 'none'}}>
      <div 
        style={{
          position: 'fixed',
          right: isMobile ? '65%' : '28rem',
          top: 'auto', 
          bottom: isMobile ? '85px' : '6.4rem',
          zIndex: 40,
          color: '#60efff',
          fontWeight: 600,
          fontSize: isMobile ? '0.8rem' : '0.9rem',
          textShadow: '0 2px 4px rgba(0,0,0,0.8)',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)',
          padding: '3px 8px',
          borderRadius: '4px',
          animation: 'attention-text 3s ease-in-out infinite'
        }}
      >
        Try it yourself!
      </div>
      <svg 
        className="chat-demo-prompt" 
        viewBox="0 0 100 100" 
        xmlns="http://www.w3.org/2000/svg" 
        style={{
          bottom: isMobile ? '40px' : '3rem',
          top: 'auto',
          right: isMobile ? '60%' : '27.5rem',
          position: 'fixed',
          zIndex: 40,
          width: isMobile ? '60px' : '80px',
          height: isMobile ? '60px' : '80px',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))',
          animation: 'attention-arrow 3s ease-in-out infinite'
        }}
      >
        <defs>
          <linearGradient id="arrow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#60efff' }} />
            <stop offset="100%" style={{ stopColor: '#c779ff' }} />
          </linearGradient>
        </defs>
        <path 
          d={isMobile 
            ? "M50,20 Q50,40 50,70 M30,50 L50,70 L70,50"
            : "M20,40 Q40,60 80,80 M55,55 L80,80 L55,85"
          }
          strokeWidth="3"
          stroke="url(#arrow-gradient)"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <style>
        {`
          @keyframes attention-text {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.8;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes attention-arrow {
            0% {
              transform: translate(0, 0) scale(1);
              opacity: 1;
            }
            50% {
              transform: translate(5px, -5px) scale(1.1);
              opacity: 0.8;
            }
            100% {
              transform: translate(0, 0) scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const featuresSectionRef = useRef(null);
  const heroSectionRef = useRef(null);
  const embedSectionRef = useRef(null);

  // Mouse tracking effect for all sections
  const handleMouseMove = (e, section) => {
    if (!section) return;
    const rect = section.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    section.style.setProperty('--mouse-x', `${x}%`);
    section.style.setProperty('--mouse-y', `${y}%`);
  };

  useEffect(() => {
    const sections = [
      { ref: heroSectionRef, element: heroSectionRef.current },
      { ref: featuresSectionRef, element: featuresSectionRef.current },
      { ref: embedSectionRef, element: embedSectionRef.current }
    ];

    sections.forEach(({ ref, element }) => {
      if (!element) return;

      const handleMouseMoveSection = (e) => handleMouseMove(e, element);
      const handleMouseLeave = () => {
        element.style.setProperty('--mouse-x', '50%');
        element.style.setProperty('--mouse-y', '50%');
      };

      element.addEventListener('mousemove', handleMouseMoveSection);
      element.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        element.removeEventListener('mousemove', handleMouseMoveSection);
        element.removeEventListener('mouseleave', handleMouseLeave);
      };
    });
  }, []);

  useEffect(() => {
    // Add dialog-chat script and styles
    const link = document.createElement('link');
    link.href = `${process.env.PUBLIC_URL}/embed-styles.css`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Load embed script
    const embedScript = document.createElement('script');
    embedScript.src = `${process.env.PUBLIC_URL}/embed.js`;
    document.body.appendChild(embedScript);

    // No cleanup to avoid React unmount issues
  }, []);

  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px'
    });

    document.querySelectorAll('.embed-feature, .embed-demo-container, .embed-title, .embed-subtitle')
      .forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page bg-background">
      <Navigation />
      <section className="hero-section" ref={heroSectionRef}>
        <div className="hero-content">
          <ChatWidget
            agentId={process.env.REACT_APP_DEMO_AGENT_ID}
            name="Lifelike Agent"
            avatar="LA"
            status="Press to speak with me!"
            onError={(error) => {
              console.error('Demo call error:', error);
              toast.error('Failed to start demo call. Please try again later.');
            }}
            className="mb-8"
          />
          <h1 className="hero-title">
            Lifelike AI Agents for<br />
            <span className="text-gradient block mt-2">
              <TypeWriter />
            </span>
          </h1>
          <p className="hero-subtitle">
            Deliver unforgettable customer experiences with AI-driven voices and seamless interactions. Boost sales, automate tasks, and impress your customers.
          </p>
          <EmailForm />
          <div className="hero-stats flex flex-wrap items-center justify-center gap-4 sm:gap-8 mt-12">
            <div className="stat-item text-center min-w-[100px]">
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1">99.9%</div>
              <div className="text-xs sm:text-sm text-gray-400">Uptime</div>
            </div>
            <div className="stat-divider hidden sm:block h-8 w-px bg-gray-800"></div>
            <div className="stat-item text-center min-w-[100px]">
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1">24/7</div>
              <div className="text-xs sm:text-sm text-gray-400">Support</div>
            </div>
          </div>
        </div>
      </section>
      <SeamlessAI />
      <Features />
      <div className="embed-section py-16 md:py-32 relative overflow-hidden" ref={embedSectionRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="embed-title text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              AI Agents for<br />
              <span className="gradient-cyan">Every Channel</span>
            </h2>
            <p className="embed-subtitle text-base md:text-lg mt-4 max-w-3xl mx-auto px-4">
              One AI agent, multiple channels. Handle customer interactions seamlessly across voice, chat, and messaging platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
            <div className="space-y-8 md:space-y-12">
              <div className="embed-feature flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="embed-feature-icon shrink-0">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Voice Calls</h3>
                  <p className="text-gray-400 mt-2">
                    Make and receive calls with natural voice interactions
                  </p>
                </div>
              </div>

              <div className="embed-feature flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="embed-feature-icon shrink-0">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Chat & Messaging</h3>
                  <p className="text-gray-400 mt-2">
                    Connect via WhatsApp, Telegram, Instagram, and Facebook
                  </p>
                </div>
              </div>

              <div className="embed-feature flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="embed-feature-icon shrink-0">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Website Integration</h3>
                  <p className="text-gray-400 mt-2">
                    Embed AI chat widgets directly on your website
                  </p>
                </div>
              </div>
            </div>

            <div className="embed-demo-container w-full max-w-lg mx-auto md:max-w-none">
              <div className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-3 md:p-4 shadow-xl border border-gray-700 relative">
                <div
                  id="dialog-chat"
                  data-dialogflow-id="bfc7c33e-cf20-4ddf-bebd-ef2070bb3bef"
                  data-start-node-id="1"
                  className="w-full h-[400px] md:h-[500px]"
                />
                <ChatDemoPrompt />
              </div>

              <div className="code-block mt-4 bg-[#0f1117] rounded-lg p-3 md:p-4 text-xs md:text-sm overflow-x-auto">
                <code className="text-purple-400 whitespace-pre-wrap break-all md:break-normal">{`<script src="https://lifelikeagents.com/embed.js"></script>
<div id="dialog-chat" data-dialogflow-id="YOUR_ID"></div>`}</code>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CallToAction />
      <Footer />
    </div>
  );
};

export default LandingPage; 